import { Heading, HeadingProps } from "@origin-digital/ods-core";
import React from "react";
import styled from "styled-components";
import { ActionBarMobileProps, getMobileNavButton } from "./ActionBarMobile";
import { Box } from "./Box";
import { ChatButton } from "./ChatButton";

const CustomHeading = ({
  className,
  children,
  component,
  "data-id": dataId,
}: HeadingProps) => (
  <Heading
    align="center"
    variant="h4"
    className={className}
    component={component}
    data-id={dataId}
    truncate
  >
    {children}
  </Heading>
);

const StyledCustomHeading = styled(CustomHeading)`
  && {
    font-size: ${({ theme }) => `${theme.typography.body1.fontSize}`};
    min-width: 0;
    width: 100%;
  }
`;

const BalancedGrid = styled(Box)<{ $isDesktop: boolean }>`
  align-items: center;
  display: grid;
  grid-template-columns: ${({ $isDesktop }) =>
    $isDesktop
      ? "minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr)"
      : "minmax(104px, 1fr) minmax(0, 2fr) minmax(104px, 1fr)"};
  min-width: 0;
  overflow: hidden;
  width: 100%;
`;

const Side = styled(Box)<{ $align: "left" | "right" }>`
  align-items: center;
  display: flex;
  justify-content: ${({ $align }) =>
    $align === "left" ? "flex-start" : "flex-end"};
  min-width: 0;
`;

const Title = styled(Box)`
  min-width: 0;
  overflow: hidden;
  padding: 0 ${({ theme }) => `${theme.space.small}px`};
  width: 100%;
`;

const PropertyLayout = styled(Box)`
  align-items: center;
  display: flex;
  min-width: 0;
  overflow: hidden;
  width: 100%;
`;

const PropertySelectorContainer = styled(Box)`
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-start;
  min-width: 0;
  overflow: hidden;

  > * {
    max-width: 100%;
    min-width: 0;
  }
`;

export const NavTitle = ({
  navButton,
  onClickMobileNavButton,
  headingComponent,
  title,
  desktopVariant,
  isDesktop,
  showPropertySelector,
  propertySelector,
  onClickChatButton,
  showChatButton = false,
}: ActionBarMobileProps) => {
  const actionButtons =
    showChatButton && onClickChatButton ? (
      <ChatButton
        onClick={onClickChatButton}
        presentation={isDesktop ? "desktop" : "mobile"}
      />
    ) : null;

  if (showPropertySelector) {
    return (
      <PropertyLayout data-id="actionbar-property-layout">
        <PropertySelectorContainer
          data-id={`actionbar-${
            isDesktop ? "desktop" : "mobile"
          }-property-selector`}
          paddingLeft="medium"
        >
          {propertySelector}
        </PropertySelectorContainer>
        {actionButtons ? (
          <Side
            $align="right"
            data-id={`actionbar-${
              isDesktop ? "desktop" : "mobile"
            }-property-actions`}
            paddingLeft="small"
            paddingRight="medium"
          >
            {actionButtons}
          </Side>
        ) : null}
      </PropertyLayout>
    );
  }

  return (
    <BalancedGrid
      $isDesktop={Boolean(isDesktop)}
      data-id="actionbar-title-layout"
    >
      <Side
        $align="left"
        data-id={`actionbar-${isDesktop ? "desktop" : "mobile"}-navigation`}
        paddingLeft={isDesktop ? "xsmall" : "xxsmall"}
      >
        {getMobileNavButton(
          navButton,
          onClickMobileNavButton,
          isDesktop,
          desktopVariant,
          showPropertySelector
        )}
      </Side>
      <Title>
        {title ? (
          <StyledCustomHeading
            data-id="actionbar-heading-mobile"
            variant="h4"
            component={headingComponent}
          >
            {title}
          </StyledCustomHeading>
        ) : null}
      </Title>
      <Side
        $align="right"
        data-id={`actionbar-${isDesktop ? "desktop" : "mobile"}-actions`}
        paddingLeft="small"
        paddingRight="medium"
      >
        {actionButtons}
      </Side>
    </BalancedGrid>
  );
};
